<template>
  <ul class="race-link-list-section">
    <li
      class="race-link-list-section__item"
      v-for="(race, i) in allRaces"
      :key="i"
      @click="toggleRaceSelect"
    >
      <button
        :disabled="
          !hasDistributedMovie(race, contentsInfos, angleMovieInfos, qualifyingRaceStartEventList)
        "
        class="race-link-list-section__link"
        :class="{
          'race-link-list-section__link--current': race.sectionId === currentSectionId,
          'race-link-list-section__link--disabled': !hasDistributedMovie(
            race,
            contentsInfos,
            angleMovieInfos,
            qualifyingRaceStartEventList,
          ),
        }"
        @click="linkTo(race)"
      >
        <span>{{ race.raceTitle }}</span>
        <img
          v-if="
            !hasDistributedMovie(race, contentsInfos, angleMovieInfos, qualifyingRaceStartEventList)
          "
          :src="require('@/assets/img/icon/icon_chevron_right_18__gray50.svg')"
          alt=""
        />
        <img v-else :src="require('@/assets/img/icon/icon_chevron_right_18__white.svg')" alt="" />
      </button>
    </li>
  </ul>
</template>

<script lang="ts">
import { computed, defineComponent, Ref } from '@vue/composition-api'
import { orderBy } from 'lodash'
import RaceDocument from '@/store/stores/collectionModule/documents/race/RaceDocument'
import StoreUtil from '@/store/StoreUtil'
import ContentsInfoDocument from '@/store/stores/collectionModule/documents/contents/ContentsInfoDocument'
import AngleMovieInfoDocument from '@/store/stores/collectionModule/documents/angleMovie/AngleMovieInfoDocument'
import HighlightDocument from '@/store/stores/collectionModule/documents/highlight/HighlightDocument'
import useMovieContentsStatus from '@/components/hook/useMovieContentsStatus'

/**
 * レース動画再生画面 レース選択リストセクションのコンポーネント
 */
export default defineComponent({
  name: 'RaceLinkListSection',
  components: {},
  setup() {
    const topPageStore = StoreUtil.useStore('TopPageStore')
    const raceVideoPageStore = StoreUtil.useStore('RaceVideoPageStore')
    const { targetRace, currentTimeMainVideoTime, clearFetchRaceVideoPageData } = raceVideoPageStore
    const { hasDistributedMovie } = useMovieContentsStatus()

    /**
     * 現在再生中のセクションID
     * 決勝、または、予選(Q1-A, Q1-B, Q2のいずれか)のセクションIDを算出する。
     */
    const currentSectionId = computed(() => {
      if (
        targetRace.value.additionalData?.raceType === 'RACE' ||
        targetRace.value.additionalData?.raceType === 'FREE_PRACTICE' ||
        targetRace.value.additionalData?.raceType === 'EVENT'
      ) {
        return targetRace.value.id
      }

      // 現在の再生位置から、現在再生ている予選の各セクション(Q1-A, Q1-B, Q2)を求める
      const sectionList = [...topPageStore.qualifyingRaceList.value]
      sectionList.sort(
        (a, b) =>
          (b.highlightPlayInfo?.movieStartTime ?? 0) - (a.highlightPlayInfo?.movieStartTime ?? 0),
      )
      const targetSection = sectionList.find(
        (section) =>
          (currentTimeMainVideoTime.value ?? 0) > (section?.highlightPlayInfo?.movieStartTime ?? 0),
      )
      return targetSection ? targetSection.sectionId : null
    })

    const allRaces = computed(() => {
      const allRacesData = [
        ...topPageStore.sessionsWithOutRace.value,
        ...topPageStore.mainRace.value,
      ]
      return orderBy(allRacesData, 'startDate')
    })

    return {
      allRaces,
      contentsInfos: topPageStore.contentsInfos as Ref<Array<ContentsInfoDocument>>,
      angleMovieInfos: topPageStore.angleMovieInfos as Ref<Array<AngleMovieInfoDocument>>,
      qualifyingRaceStartEventList: topPageStore.qualifyingRaceStartEventList as Ref<
        Array<HighlightDocument>
      >,
      hasDistributedMovie,
      currentSectionId,
      clearFetchRaceVideoPageData,
    }
  },
  methods: {
    /**
     * レース選択非表示化
     * @event toggleRaceSelect レースが選択された場合に発生する
     */
    toggleRaceSelect(): void {
      this.$emit('raceSelectToggle', 'off')
    },
    /**
     * 指定されたレースの動画再生画面に遷移する。
     * @param race レース
     */
    linkTo(race: RaceDocument) {
      // そのまま遷移すると、動画再生画面の状態が残ってしまい動画再生が正しく行われないため、一度TOP画面に遷移する。
      this.$router.replace({ name: 'TopPage' }).then(() => {
        const targetPath = race.highlightPlayInfo?.highlightId
          ? `/race-video/${race.championshipMasterId}/${race.id}/${race.highlightPlayInfo.highlightId}`
          : `/race-video/${race.championshipMasterId}/${race.id}/`

        this.$router.replace({ path: targetPath })
      })
    },
  },
})
</script>

<style lang="scss" scoped>
.race-link-list-section {
  width: 100%;
  font-size: 14px;
  border-top: 1px solid rgba(#fff, 0.1);
  border-bottom: 1px solid rgba(#fff, 0.1);

  &__item {
    width: 100%;

    &:not(:first-child) {
      border-top: 1px solid rgba(#fff, 0.1);
    }
  }

  &__link {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 16px 13px 16px 0;
    color: $white;

    &--current {
      color: $green;
    }

    // TODO: SFGO-XXX 最終的には鍵アイコンで表現されるのでデザインがfixされたら対応する。現状、テキストの色を暗めにして表現
    &--disabled {
      color: #808080;
    }
  }
}
</style>
